import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import { OperationLogLine } from "gc-web-proto/galaxycompletepb/apipb/domainpb/operation_pb";
import { ProjectHostActionTestExecutionStatus } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/project_host_action_test_execution_status_pb";
import {
    CreateProjectHostAction,
    CreateProjectHostActionTest,
    DeleteProjectHostAction,
    GetProjectHostActionDetails,
    GetProjectHostActionTestDetails,
    ListProjectHostActionTests,
    ListProjectHostActionTestLogs,
    ListProjectHostActions,
    UpdateProjectHostAction,
} from "gc-web-proto/galaxycompletepb/apipb/projecthostaction_api_pb";
import {
    ApproveOperationTask,
    ArchiveOperation,
    CancelOperation,
    CreateOperations,
    GetOperationConfig,
    GetOperationDetails,
    GetOperationStatusSummary,
    GetOperationXRay,
    ListOperationConfigVersions,
    ListOperationLogLines,
    ListProjectOperations,
    RetryOperation,
    UnarchiveOperation,
    UpdateOperationConfig,
} from "gc-web-proto/galaxycompletepb/apipb/operation_api_pb";
import { GetOperationTaskFact } from "gc-web-proto/galaxycompletepb/apipb/support_api_pb";
import { PagerParams } from "gc-web-proto/galaxycompletepb/commonpb/datafilter_pb";
import { ListGalaxyMigrateDeployments } from "gc-web-proto/galaxycompletepb/apipb/deployment_api_pb";
import { MtdiOsType } from "gc-web-proto/galaxycompletepb/apipb/domainpb/enumpb/mtdi_os_type_pb";
import { JavaScriptValue } from "google-protobuf/google/protobuf/struct_pb";
import { create } from "zustand";
import { MigrateOpsMutationKeys, MigrateOpsQueryKeys } from "../../common/QueryKeys";
import { useGlobalTableSortFilterState } from "../../common/table/TableFilterState";
import { useMutationAlertError, useMutationTrackAndAlertError } from "../core/data/useHooksWithErrorHandling";
import { useGrpcApiStore } from "../grpc/grpcApiStore";
import { useCurrentProjectID } from "../project/CurrentProjectState";

export const useListMigrateOps = (projectId: string, includeArchived: boolean, page: number, perPage: number) => {
    const apis = useGrpcApiStore();
    const sortFilterState = useGlobalTableSortFilterState().tableStateMap.get(MigrateOpsQueryKeys.listProjectOperations);
    const req = new ListProjectOperations.Request()
        .setProjectId(projectId)
        .setIncludeArchived(includeArchived)
        .setPageParams(new PagerParams().setPage(page).setPerPage(perPage));
    const queryFn = async () => {
        if (sortFilterState.sortState) {
            req.setSortParams(sortFilterState.sortFilterConfig.sort.getSortParam(sortFilterState.sortState));
        }
        for (let filter of sortFilterState.filters || []) {
            filter.fieldConfig.addFilterToRequest(req, filter.param);
        }

        const res = await apis.operationService.listProjectOperations(req, null);
        return res?.toObject();
    };

    return useQuery({
        queryKey: [
            MigrateOpsQueryKeys.listProjectOperations,
            projectId,
            includeArchived,
            page,
            perPage,
            sortFilterState.sortState?.field.label,
            sortFilterState.sortState?.descending.label,
            sortFilterState.filters.map((f) => `${f.fieldConfig.label}: ${JSON.stringify(f.param.toObject())}`),
        ],
        queryFn: queryFn,
        refetchInterval: 30 * 1000,
    });
};

export const useGetOperationStatusSummary = (projectId: string, includeArchived: boolean) => {
    const apis = useGrpcApiStore();
    return useQuery({
        queryKey: [MigrateOpsQueryKeys.getOperationStatusSummary, projectId, includeArchived],
        queryFn: async () => {
            const req = new GetOperationStatusSummary.Request().setProjectId(projectId).setIncludeArchived(includeArchived);
            const res = await apis.operationService.getOperationStatusSummary(req, null);
            return res?.toObject();
        },
        refetchInterval: 30 * 1000,
    });
};

export const useGetMigrateOpDetails = (opId: number, refetchInterval?: number) => {
    const apis = useGrpcApiStore();
    const req = new GetOperationDetails.Request().setOperationId(opId).setIncludeOperationConfig(true);
    const queryFn = async () => {
        const res = await apis.operationService.getOperationDetails(req, null);
        return res?.toObject();
    };
    return useQuery({
        queryKey: [MigrateOpsQueryKeys.getOperationDetails, opId],
        queryFn: queryFn,
        refetchInterval: refetchInterval || 60 * 1000,
    });
};
export const useGetMigrateOpConfig = (opId: number) => {
    const apis = useGrpcApiStore();
    const req = new GetOperationConfig.Request().setOperationId(opId).setReturnYamlString(true);
    const queryFn = async () => {
        const res = await apis.operationService.getOperationConfig(req, null);
        return res?.toObject();
    };
    return useQuery({
        queryKey: [MigrateOpsQueryKeys.getOperationConfig, opId],
        queryFn: queryFn,
    });
};
export type CustomOperationLogLine = Omit<OperationLogLine.AsObject, "data"> & { data: { [key: string]: JavaScriptValue } };
export type ListOperationLogLinesResponse = Omit<ListOperationLogLines.Response.AsObject, "itemsList"> & { itemsList: Array<CustomOperationLogLine> };

export const useListOperationLogLines = (opId: number, taskId?: number, queryInterval?: number) => {
    const apis = useGrpcApiStore();
    const req = new ListOperationLogLines.Request().setOperationId(opId).setPart(1).setLatestFirst(true);

    if (!!taskId) {
        req.setOperationTaskId(taskId);
    }
    const queryFn = async () => {
        const res = await apis.operationService.listOperationLogLines(req, null);
        const items = res.getItemsList().map((item): CustomOperationLogLine => {
            return {
                ...item.toObject(),
                data: item?.getData()?.toJavaScript(),
            };
        });
        if (req.getLatestFirst()) {
            items.reverse();
        }
        const resObject: ListOperationLogLinesResponse = {
            ...res.toObject(),
            itemsList: items,
        };
        return resObject;
    };

    return useQuery({
        queryKey: [MigrateOpsQueryKeys.listOperationLogLines, opId, taskId],
        queryFn: queryFn,
        refetchInterval: queryInterval || 60 * 1000,
    });
};

export const useCreateNewOperationFromYaml = () => {
    const apis = useGrpcApiStore();
    const queryClient = useQueryClient();
    const projectId = useCurrentProjectID();
    const mutationFn = async (yamlString: string) => {
        const req = new CreateOperations.Request().setYamlString(yamlString).setProjectId(projectId);
        const res = await apis.operationService.createOperations(req, null);
        return res?.toObject();
    };
    return useMutationTrackAndAlertError({
        mutationKey: [MigrateOpsMutationKeys.createOperationFromYaml],
        mutationFn: mutationFn,
        onSuccess: async () => {
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.listProjectOperations],
            });
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.getOperationStatusSummary],
            });
        },
    });
};

export const useRetryOperation = () => {
    const apis = useGrpcApiStore();
    const queryClient = useQueryClient();

    const mutationFn = async (opId: number) => {
        const req = new RetryOperation.Request().setOperationId(opId);
        const res = await apis.operationService.retryOperation(req, null);
        return res?.toObject();
    };
    return useMutationTrackAndAlertError({
        mutationKey: [MigrateOpsMutationKeys.retryOperation],
        mutationFn: mutationFn,
        onSuccess: async () => {
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.getOperationDetails],
            });
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.getOperationStatusSummary],
            });
        },
    });
};

export const useApproveOperationTask = () => {
    const apis = useGrpcApiStore();
    const queryClient = useQueryClient();

    const mutationFn = async (req: ApproveOperationTask.Request) => {
        const res = await apis.operationService.approveOperationTask(req, null);
        return res?.toObject();
    };
    return useMutationTrackAndAlertError({
        mutationKey: [MigrateOpsMutationKeys.approveTask],
        mutationFn: mutationFn,
        onSuccess: async () => {
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.getOperationDetails],
            });
        },
    });
};

export const useGetMigrateOpsDocs = () => {
    const queryFn = async () => {
        const res = await fetch("/apidocs/apidocs.swagger.json", {});
        return res.json();
    };

    return useQuery({
        queryKey: [MigrateOpsQueryKeys.getOperationDocs],
        queryFn: queryFn,
    });
};

export const useUpdateOperationConfig = () => {
    const apis = useGrpcApiStore();
    const queryClient = useQueryClient();

    const mutationFn = async (req: UpdateOperationConfig.Request) => {
        const res = await apis.operationService.updateOperationConfig(req, null);
        return res?.toObject();
    };
    return useMutationTrackAndAlertError({
        mutationKey: [MigrateOpsMutationKeys.updateOperationConfig],
        mutationFn: mutationFn,
        onSuccess: async (data, variables) => {
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.getOperationConfig, variables.getOperationId()],
            });
        },
    });
};

export const useCancelOperation = () => {
    const apis = useGrpcApiStore();
    const queryClient = useQueryClient();

    const mutationFn = async (opId: number) => {
        const req = new CancelOperation.Request().setOperationId(opId);
        const res = await apis.operationService.cancelOperation(req, null);
        return res?.toObject();
    };
    return useMutationTrackAndAlertError({
        mutationKey: [MigrateOpsMutationKeys.cancelOperation],
        mutationFn: mutationFn,
        onSuccess: async () => {
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.getOperationDetails],
            });
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.getOperationStatusSummary],
            });
        },
    });
};

export const useGetOperationTaskFacts = (taskId: number) => {
    const apis = useGrpcApiStore();
    const queryFn = async () => {
        const req = new GetOperationTaskFact.Request().setOperationTaskId(taskId);
        const res = await apis.supportService.getOperationTaskFact(req, null);
        return res.toObject();
    };

    return useQuery({
        queryKey: [MigrateOpsQueryKeys.getOperationTaskFacts, taskId],
        queryFn: queryFn,
    });
};

interface MigrateOpsDocsDrawerState {
    migrateOpsDocsDrawerOpen: boolean;
    setMigrateOpsDocsDrawerOpen: (open: boolean) => void;
}

export const useMigrateOpsDocsDrawerState = create<MigrateOpsDocsDrawerState>((set) => ({
    migrateOpsDocsDrawerOpen: false,
    setMigrateOpsDocsDrawerOpen: (open: boolean) =>
        set({
            migrateOpsDocsDrawerOpen: open,
        }),
}));

export const useArchiveOperation = () => {
    const apis = useGrpcApiStore();
    const queryClient = useQueryClient();

    const mutationFn = async (opId: number) => {
        const req = new ArchiveOperation.Request().setOperationId(opId);
        const res = await apis.operationService.archiveOperation(req, null);
        return res?.toObject();
    };
    return useMutationTrackAndAlertError({
        mutationKey: [MigrateOpsMutationKeys.archiveOperation],
        mutationFn: mutationFn,
        onSuccess: async () => {
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.getOperationDetails],
            });
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.listProjectOperations],
            });
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.getOperationStatusSummary],
            });
        },
    });
};

export const useUnarchiveOperation = () => {
    const apis = useGrpcApiStore();
    const queryClient = useQueryClient();

    const mutationFn = async (opId: number) => {
        const req = new UnarchiveOperation.Request().setOperationId(opId);
        const res = await apis.operationService.unarchiveOperation(req, null);
        return res?.toObject();
    };
    return useMutationTrackAndAlertError({
        mutationKey: [MigrateOpsMutationKeys.unarchiveOperation],
        mutationFn: mutationFn,
        onSuccess: async () => {
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.getOperationDetails],
            });
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.listProjectOperations],
            });
            await queryClient.refetchQueries({
                queryKey: [MigrateOpsQueryKeys.getOperationStatusSummary],
            });
        },
    });
};

export const useListOperationConfigVersions = (opId: number, page: number, perPage: number) => {
    const apis = useGrpcApiStore();
    const queryFn = async () => {
        const req = new ListOperationConfigVersions.Request()
            .setOperationId(opId)
            .setPageParams(new PagerParams().setPage(page).setPerPage(perPage))
            .setReturnConfigAsYamlString(true);
        const res = await apis.operationService.listOperationConfigVersions(req, null);
        return res.toObject();
    };
    return useQuery({
        queryKey: [MigrateOpsQueryKeys.listOperationConfigVersions, opId, page, perPage],
        queryFn: queryFn,
    });
};

export const useGetOperationXRay = (opId: number) => {
    const apis = useGrpcApiStore();
    const queryFn = async () => {
        const req = new GetOperationXRay.Request().setOperationId(opId);
        const res = await apis.operationService.getOperationXRay(req, null);
        return res?.toObject();
    };
    return useQuery({
        queryKey: [MigrateOpsQueryKeys.getOperationXRay, opId],
        queryFn: queryFn,
    });
};

export const useListProjectHostActions = (projectId: string, page: number, perPage: number) => {
    const apis = useGrpcApiStore();
    const queryFn = async () => {
        const req = new ListProjectHostActions.Request().setProjectId(projectId).setPageParams(new PagerParams().setPage(page).setPerPage(perPage));
        const res = await apis.projectHostActionService.listProjectHostActions(req, null);
        return res.toObject();
    };
    return useQuery({
        queryKey: [MigrateOpsQueryKeys.listProjectHostActions, projectId, page, perPage],
        queryFn: queryFn,
    });
};

export const useGetProjectHostActionDetails = (projectId: string, reference: string) => {
    const apis = useGrpcApiStore();
    const queryFn = async () => {
        const req = new GetProjectHostActionDetails.Request().setProjectId(projectId).setReference(reference);
        const res = await apis.projectHostActionService.getProjectHostActionDetails(req, null);
        return res.toObject();
    };
    return useQuery({
        queryKey: [MigrateOpsQueryKeys.getProjectHostActionDetails, projectId, reference],
        queryFn: queryFn,
        enabled: !!projectId && !!reference,
    });
};

export const useCreateProjectHostAction = () => {
    const apis = useGrpcApiStore();

    const mutationFn = async (req: CreateProjectHostAction.Request) => {
        const res = await apis.projectHostActionService.createProjectHostAction(req, null);
        return res.toObject();
    };

    return useMutationTrackAndAlertError({
        mutationKey: [MigrateOpsMutationKeys.createProjectHostAction],
        mutationFn: mutationFn,
    });
};

export const useDeleteProjectHostAction = () => {
    const apis = useGrpcApiStore();

    const mutationFn = async (req: DeleteProjectHostAction.Request) => {
        const res = await apis.projectHostActionService.deleteProjectHostAction(req, null);
        return res.toObject();
    };
    return useMutationTrackAndAlertError({
        mutationKey: [MigrateOpsMutationKeys.deleteProjectHostAction],
        mutationFn: mutationFn,
    });
};

export const useCreateProjectHostActionTest = () => {
    const apis = useGrpcApiStore();
    const queryClient = useQueryClient();
    const mutationFn = async (req: CreateProjectHostActionTest.Request) => {
        const res = await apis.projectHostActionService.createProjectHostActionTest(req, null);
        return res.toObject();
    };

    return useMutationAlertError({
        mutationKey: [MigrateOpsMutationKeys.createProjectHostActionTest],
        mutationFn: mutationFn,
        onSuccess: async (_data, variables) => {
            await queryClient.invalidateQueries({
                queryKey: [MigrateOpsQueryKeys.listProjectHostActionTests, variables.getProjectId()],
                refetchType: "active",
            });
        },
    });
};

export const useListProjectHostActionTestLogs = (executionId: number, projectId: string) => {
    const apis = useGrpcApiStore();
    return useInfiniteQuery({
        queryKey: [MigrateOpsQueryKeys.listProjectHostActionTestLogs, executionId, projectId],
        initialPageParam: 1,
        queryFn: async ({ pageParam }) => {
            const req = new ListProjectHostActionTestLogs.Request().setExecutionId(executionId).setPart(pageParam).setProjectId(projectId);
            return (await apis.projectHostActionService.listProjectHostActionTestLogs(req, null)).toObject();
        },
        getNextPageParam: (lastPage, _pages, lastPageParam) => (lastPage.hasMore ? lastPageParam + 1 : undefined),
    });
};

export const useUpdateProjectHostAction = () => {
    const apis = useGrpcApiStore();

    const mutationFn = async (req: UpdateProjectHostAction.Request) => {
        const res = await apis.projectHostActionService.updateProjectHostAction(req, null);
        return res.toObject();
    };
    return useMutationTrackAndAlertError({
        mutationKey: [MigrateOpsMutationKeys.updateProjectHostAction],
        mutationFn: mutationFn,
    });
};

export const useListProjectHostActionTestTargets = (
    projectId: string,
    os: MtdiOsType.MtdiOsType | undefined,
    page: number,
    perPage: number,
    enabled: boolean
) => {
    const apis = useGrpcApiStore();
    return useQuery({
        queryKey: [MigrateOpsQueryKeys.listProjectHostActionTestTargets, projectId, os, page, perPage],
        enabled: enabled && os !== undefined,
        queryFn: async () => {
            const req = new ListGalaxyMigrateDeployments.Request()
                .setProjectId(projectId)
                .setOnlyConnected(true)
                .setOnlyHostopsTestingEnabled(true)
                .setPageParams(new PagerParams().setPage(page).setPerPage(perPage));
            if (os !== undefined) {
                req.setOs(os);
            }
            return (await apis.deploymentService.listGalaxyMigrateDeployments(req, null)).toObject();
        },
    });
};

export const useListProjectHostActionTests = (projectId: string, page: number, perPage: number, reference?: string) => {
    const apis = useGrpcApiStore();
    const queryFn = async () => {
        const req = new ListProjectHostActionTests.Request().setProjectId(projectId).setPageParams(new PagerParams().setPage(page).setPerPage(perPage));
        if (reference) {
            req.setReference(reference);
        }
        const res = await apis.projectHostActionService.listProjectHostActionTests(req, null);
        return res.toObject();
    };
    return useQuery({
        queryKey: [MigrateOpsQueryKeys.listProjectHostActionTests, projectId, page, perPage, reference],
        queryFn: queryFn,
        refetchInterval: (query) =>
            query.state.data?.itemsList.some(
                (item) =>
                    item.execution?.status === ProjectHostActionTestExecutionStatus.ProjectHostActionTestExecutionStatus.PENDING ||
                    item.execution?.status === ProjectHostActionTestExecutionStatus.ProjectHostActionTestExecutionStatus.RUNNING
            )
                ? 3 * 1000
                : false,
    });
};

export const useGetProjectHostActionTestDetails = (projectId: string, executionId: number) => {
    const apis = useGrpcApiStore();
    const queryFn = async () => {
        const req = new GetProjectHostActionTestDetails.Request().setProjectId(projectId).setExecutionId(executionId);
        const res = await apis.projectHostActionService.getProjectHostActionTestDetails(req, null);
        return res.toObject();
    };
    return useQuery({
        queryKey: [MigrateOpsQueryKeys.getProjectHostActionTestDetails, projectId, executionId],
        queryFn: queryFn,
    });
};
